import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, XCircle } from "lucide-react";
import { Switch } from "../../ui/switch";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  CTM_IMPORT_COLUMNS,
  CTM_IMPORT_SAMPLE,
  CTM_IMPORT_SHEET_NAME,
  ctmImportLoadingDefaultState,
} from "../../../utils/constants/CategoryTrackMapping";
import {
  GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY,
  GET_CTM_OPTIONS_QUERY_KEY,
} from "../../../utils/constants/queryKeys";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import CategoryTrackMappingAPIs from "../../../apiRoutes/CategoryTrackMapping";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import usePermissions from "../../../hooks/usePermissions";
import CustomModal from "../../common/CustomModal";
import CtmImportRow from "./CtmImportRow";

const KEY_BY_HEADER = Object.fromEntries(
  CTM_IMPORT_COLUMNS.flatMap(({ key, aliases }) =>
    aliases.map((a) => [a.toLowerCase(), key])
  )
);

const handleReadRows = async ({ file }) => {
  const book = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    raw: true,
  });
  const sheetName = book.SheetNames.includes(CTM_IMPORT_SHEET_NAME)
    ? CTM_IMPORT_SHEET_NAME
    : book.SheetNames[0];
  return XLSX.utils
    .sheet_to_json(book.Sheets[sheetName], { defval: "", raw: false })
    .map((r) =>
      Object.entries(r).reduce((acc, [header, value]) => {
        const key = KEY_BY_HEADER[String(header).trim().toLowerCase()];
        if (key) acc[key] = String(value ?? "").trim();
        return acc;
      }, {})
    )
    .filter((r) => Object.values(r).some(Boolean));
};

/** One upload for tracks and their campaigns: check every row first, then save all or nothing. */
const BulkUploadCTMModal = ({ onClose }) => {
  const [fileName, setFileName] = useState("");
  const [check, setCheck] = useState(null);
  const [isErrorsOnly, setIsErrorsOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(ctmImportLoadingDefaultState);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = usePermissions();
  const errorCount = check ? check.rows.filter((r) => !r.ok).length : 0;
  const okCount = check ? check.rows.length - errorCount : 0;
  const shownRows = rows
    .map((row, index) => ({ row, index, result: check?.rows?.[index] }))
    .filter(({ result }) => !isErrorsOnly || (result && !result.ok));

  const handleDownloadTemplate = () => {
    const lines = [
      CTM_IMPORT_COLUMNS.map((c) => c.label),
      ...CTM_IMPORT_SAMPLE,
    ];
    const blob = new Blob([`${lines.map((l) => l.join(",")).join("\n")}\n`], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "track-campaign-mapping-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCheck = async ({ parsed }) => {
    setCheck(null);
    setIsLoading((prev) => ({ ...prev, checking: true }));
    try {
      setCheck(
        await CategoryTrackMappingAPIs.handleImport({
          axiosPrivate,
          rows: parsed,
          dry_run: true,
        })
      );
    } catch {
      setCheck(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, checking: false }));
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const parsed = await handleReadRows({ file });
    if (!parsed.length)
      return toast.error(
        `No rows found. Columns: ${CTM_IMPORT_COLUMNS.map((c) => c.label).join(", ")}.`
      );
    setFileName(file.name);
    setIsErrorsOnly(false);
    setRows(parsed);
    handleCheck({ parsed });
  };

  const handleSave = async () => {
    if (!check || errorCount)
      return toast.error(
        "Fix the rows marked in red, then upload the file again."
      );
    setIsLoading((prev) => ({ ...prev, saving: true }));
    try {
      const result = await CategoryTrackMappingAPIs.handleImport({
        axiosPrivate,
        rows,
        dry_run: false,
      });
      toast.success(
        `Saved: ${result?.tracks_new ?? 0} new track(s), ${result?.tracks_updated ?? 0} updated, ${result?.saved ?? 0} campaign(s) in ${result?.lists ?? 0} list(s).`
      );
      queryClient.invalidateQueries({
        queryKey: GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: GET_CTM_OPTIONS_QUERY_KEY });
      onClose();
    } catch {
      setIsLoading((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <CustomModal
      open
      title="Bulk upload tracks and campaigns"
      onClose={onClose}
      isLoading={isLoading.saving || isLoading.checking}
      handleConfirmation={handleSave}
      handleConfirmationBtnText="Save"
      className="md:!w-[85%] lg:!w-[75%]"
    >
      <div className="space-y-4 py-2">
        <div className="space-y-1 text-sm text-gray-600">
          <p>
            One row per campaign, with the track's details on every row. A row
            with no campaign columns just adds or updates the track. Each track
            + ad account in the file gets exactly the campaigns listed for it,
            in the Order given; everything not in the file stays as it is.
          </p>
          <p className="text-xs text-gray-500">
            Columns: {CTM_IMPORT_COLUMNS.map((c) => c.label).join(", ")}. The
            nomenclature Excel works too.
            {!isSuperAdmin && " Campaign columns need a super admin."} Nothing
            is saved unless every row passes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFile}
            className="max-w-sm"
          />
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download className="mr-2 h-4 w-4" />
            Download template
          </Button>
          {fileName && (
            <span className="text-xs text-gray-500">
              {fileName} · {rows.length} row(s)
              {isLoading.checking && " · checking…"}
            </span>
          )}
        </div>
        {check && !isLoading.checking && (
          <div
            className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-3 py-2 text-xs ${errorCount ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}
          >
            <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {okCount} OK
            </span>
            {errorCount > 0 && (
              <span className="inline-flex items-center gap-1 font-medium text-rose-700">
                <XCircle className="h-4 w-4" />
                {errorCount} to fix
              </span>
            )}
            <span className="text-gray-600">
              {check.tracks_new} new track(s) · {check.tracks_updated} updated ·{" "}
              {check.lists} campaign list(s)
            </span>
            {errorCount > 0 && (
              <label className="ml-auto inline-flex items-center gap-2 text-gray-700">
                <Switch
                  checked={isErrorsOnly}
                  onCheckedChange={setIsErrorsOnly}
                  aria-label="Show only rows to fix"
                />
                Show only rows to fix
              </label>
            )}
          </div>
        )}
        {rows.length > 0 && (
          <div className="max-h-[50vh] overflow-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse text-left text-xs">
              <thead className="sticky top-0 z-[2] bg-gray-50">
                <tr>
                  <th className="sticky left-0 bg-gray-50 px-2 py-2 font-semibold text-gray-600">
                    #
                  </th>
                  {CTM_IMPORT_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className="whitespace-nowrap px-2 py-2 font-semibold text-gray-600"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownRows.map(({ row, index, result }) => (
                  <CtmImportRow
                    key={index}
                    row={row}
                    index={index}
                    result={result}
                    isChecking={isLoading.checking}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default BulkUploadCTMModal;
